// @givmo/compliance — the reads every filing screen composes (CCM-R1-INTAKE (a)): Filings, the
// filing panel, and the Calendar. One hook, one set of query keys, so the panel opened over a list
// reads nothing the list has not already read.

import { useQueries, useQuery } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import type { ComplianceOperations } from './compliance-context';
import { FILING_YEAR_SEARCH_KEY } from './compliance-routes';
import {
  decodeProfile,
  extractObligations,
  extractOccurrences,
  extractRegistrationImports,
  isModuleUnavailable,
  type ComplianceObligation,
  type ComplianceOccurrence,
  type ComplianceProfile,
  type RegistrationImport,
} from './compliance-decode';
import {
  decodeOwnFilingDetail,
  extractFilingGroups,
  extractFilingOptions,
  extractOwnFilings,
  extractRenewalCycles,
  isOwnFilingShown,
  type FilingGroup,
  type OwnFiling,
  type OwnFilingDetail,
  type RenewalCycle,
} from './compliance-filings-decode';
import {
  buildFilingRows,
  composeFilingGroups,
  lacksFiscalYearEnd,
  localIsoDate,
  ruleShapeKey,
  type FilingGroupView,
  type FilingRow,
} from './compliance-filings-model';
import {
  complianceProfileQueryKey,
  filingOptionsQueryKey,
  listAllFilingGroups,
  listAllFilingOptions,
  listAllObligations,
  listAllOccurrences,
  listAllOrganizationFilings,
  listAllRegistrationImports,
  listAllRenewalCycles,
  listAllSubmissionFollowThrough,
  submissionFollowThroughQueryKey,
} from './compliance-queries';
import { decodeCaseFollowThrough, type CaseFollowThrough } from './compliance-submission-decode';

/**
 * What is known about each year's latest send (CCM-R1-RESULTS-WEB, the organization-wide read): the
 * panel's "Next due" and tiles and the Calendar read a year's, Filings' Last filing cell a filing's
 * latest of any year. Where several cases sent for one year, the latest send speaks for it.
 */
export interface FollowThroughIndex {
  forYear(obligationId: string, cycleId: string | undefined): CaseFollowThrough | undefined;
  latest(obligationId: string): CaseFollowThrough | undefined;
}

/** The later of two sends: by the day it went, then by how many went before it. */
function laterSend(a: CaseFollowThrough, b: CaseFollowThrough): CaseFollowThrough {
  if (a.sentOn !== b.sentOn) return a.sentOn > b.sentOn ? a : b;
  return a.earlierSends >= b.earlierSends ? a : b;
}

export function followThroughIndex(rows: readonly CaseFollowThrough[]): FollowThroughIndex {
  const byYear = new Map<string, CaseFollowThrough>();
  const byFiling = new Map<string, CaseFollowThrough>();
  for (const row of rows) {
    const key = `${row.obligationId}|${row.renewalCycleId ?? ''}`;
    const year = byYear.get(key);
    byYear.set(key, year === undefined ? row : laterSend(year, row));
    const filing = byFiling.get(row.obligationId);
    byFiling.set(row.obligationId, filing === undefined ? row : laterSend(filing, row));
  }
  return {
    forYear: (obligationId, cycleId) => byYear.get(`${obligationId}|${cycleId ?? ''}`),
    latest: (obligationId) => byFiling.get(obligationId),
  };
}

const NO_FOLLOW_THROUGH: FollowThroughIndex = followThroughIndex([]);

/**
 * Each library filing's latest send of any year, on its row: for Filings' Last filing cell (U3)
 * and the Calendar's last-send line.
 */
export function withLatestSends(
  rows: readonly FilingRow[],
  followThrough: FollowThroughIndex,
): FilingRow[] {
  return rows.map((row) => {
    const latestSend = row.ref.kind === 'library' ? followThrough.latest(row.ref.id) : undefined;
    return latestSend !== undefined ? { ...row, latestSend } : row;
  });
}

/**
 * Each rule's deadline shapes (`ruleShapeKey` → its deadlines' bases) from the filing-options read:
 * what tells a library filing's kind while no fiscal year end is recorded
 * (CCM-R1-INTAKE-NODATE-FYE), for the Filings composition and the signer page alike.
 */
export function ruleShapesFromOptions(data: unknown): Map<string, string[]> {
  return new Map(
    extractFilingOptions(data).map(
      (option) =>
        [
          ruleShapeKey(option.jurisdictionCode, option.ruleId),
          option.nextDates.map((entry) => entry.dueDateBasis),
        ] as const,
    ),
  );
}

/** The groups read's key, spelled once: every group change invalidates it. */
export function filingGroupsQueryKey(orgRef: string | null) {
  return ['compliance', 'filing-groups', orgRef] as const;
}

/** An own filing's detail read's key, shared by the list and the filing's panel. */
export function organizationFilingQueryKey(orgRef: string | null, filingId: string) {
  return ['compliance', 'organization-filing', orgRef, filingId] as const;
}

/** What the panel reads of one own filing's detail; the number's history may be absent. */
export type OwnFilingRecords = Pick<OwnFilingDetail, 'cycles' | 'events'> &
  Partial<Pick<OwnFilingDetail, 'firstEnteredNumber' | 'numberCorrections'>>;

/** Everything the reads decode to, once all of them have answered. */
export interface FilingsRecords {
  obligations: ComplianceObligation[];
  occurrences: ComplianceOccurrence[];
  cycles: RenewalCycle[];
  imports: RegistrationImport[];
  ownFilings: OwnFiling[];
  /**
   * Each shown own filing's periods, history, first number and number changes, by filing id
   * (CCM-R1-OWN-FILINGS-HISTORY: the panel's corrections read the last two).
   */
  ownFilingDetails: Map<string, OwnFilingRecords>;
  groups: FilingGroup[];
  profile: ComplianceProfile | undefined;
  /**
   * Each rule's deadline shapes (`ruleShapeKey` → its deadlines' bases), from the filing options:
   * read only while no fiscal year end is recorded, since the register then serves no cycle or
   * occurrence to tell a filing's kind by (CCM-R1-INTAKE-NODATE-FYE). Absent otherwise, and when
   * that read failed (each filing then asks for the year end, as before).
   */
  ruleShapes?: Map<string, string[]>;
  today: string;
}

export interface FilingsComposition {
  loading: boolean;
  /** The first read that failed, if any. */
  failed: unknown;
  /** Whether that failure is the module being absent from this environment. */
  moduleDark: boolean;
  records?: FilingsRecords;
  rows?: FilingRow[];
  /** Every group, empty ones included (a screen shows only those holding a filing). */
  groups?: FilingGroupView[];
  /**
   * What is known about each year's latest send (CCM-R1-RESULTS-WEB). Empty where the read is dark
   * or failed: each line then says what the register says, as before, and never more.
   */
  followThrough?: FollowThroughIndex;
  /**
   * The lists naming the filings are being read again (an act marked every read stale): a filing
   * they don't hold yet is not missing until that read lands (the sibling of
   * CCM-R1-WORKBENCH-PREPARE-SETTLE: "Add filing" opens the filing it adds at once).
   */
  refreshing?: boolean;
}

/**
 * The seven list reads, each exhausted, plus each shown own filing's detail (the list serves only
 * an own filing's open period, so its last filing lives in the detail), composed into rows and
 * groups. Nothing is composed until every read has answered: a partial list is never shown as
 * whole.
 */
export function useFilingsComposition(
  operations: ComplianceOperations,
  orgRef: string | null,
): FilingsComposition {
  const enabled = orgRef !== null;
  const ref = orgRef ?? '';
  const obligations = useQuery({
    queryKey: ['compliance', 'obligations', orgRef],
    queryFn: () => listAllObligations(operations, ref),
    enabled,
  });
  const occurrences = useQuery({
    queryKey: ['compliance', 'occurrences', orgRef],
    queryFn: () => listAllOccurrences(operations, ref),
    enabled,
  });
  const imports = useQuery({
    queryKey: ['compliance', 'registration-imports', orgRef],
    queryFn: () => listAllRegistrationImports(operations, ref),
    enabled,
  });
  // Every cycle, unfiltered: the list reads each library filing's years at once.
  const cycles = useQuery({
    queryKey: ['compliance', 'renewal-cycles', orgRef],
    queryFn: () => listAllRenewalCycles(operations, ref),
    enabled,
  });
  const ownFilings = useQuery({
    queryKey: ['compliance', 'organization-filings', orgRef],
    queryFn: () => listAllOrganizationFilings(operations, ref),
    enabled,
  });
  const groups = useQuery({
    queryKey: filingGroupsQueryKey(orgRef),
    queryFn: () => listAllFilingGroups(operations, ref),
    enabled,
  });
  const profile = useQuery({
    queryKey: complianceProfileQueryKey(orgRef),
    queryFn: () => operations.getProfile(ref),
    enabled,
  });
  // With no fiscal year end recorded, only the rules' deadline shapes tell a filing with no date by
  // nature from one waiting on the year end (the add sheet's own read, under its key). Waited for,
  // so no filing flashes from one answer to the other; a failure is no answer, never the page's.
  const withoutYearEnd =
    profile.data !== undefined && lacksFiscalYearEnd(decodeProfile(profile.data));
  const options = useQuery({
    queryKey: filingOptionsQueryKey(orgRef),
    queryFn: () => listAllFilingOptions(operations, ref),
    enabled: enabled && withoutYearEnd,
  });
  const shownOwnFilingIds = extractOwnFilings(ownFilings.data)
    .filter(isOwnFilingShown)
    .map((filing) => filing.filingId);
  // CCM-R1-RESULTS-WEB: waited for, so no line flashes from the register's to the follow-through's;
  // its failure is no answer (the register's lines stand), never the page's.
  const submissions = useQuery({
    queryKey: submissionFollowThroughQueryKey(orgRef),
    queryFn: () => listAllSubmissionFollowThrough(operations, ref),
    enabled,
    retry: false,
  });
  const details = useQueries({
    queries: shownOwnFilingIds.map((filingId) => ({
      queryKey: organizationFilingQueryKey(orgRef, filingId),
      queryFn: () => operations.getOrganizationFiling(ref, filingId),
      enabled,
    })),
  });

  const reads = [
    obligations,
    occurrences,
    imports,
    cycles,
    ownFilings,
    groups,
    profile,
    ...details,
  ];
  const failed = reads.find((read) => read.error !== null && read.error !== undefined)?.error;
  const moduleDark = failed !== undefined && isModuleUnavailable(failed);
  const loading =
    reads.some((read) => read.isPending) ||
    (withoutYearEnd && options.isPending) ||
    submissions.isPending;
  if (loading || failed !== undefined) return { loading, failed, moduleDark };

  const ruleShapes =
    withoutYearEnd && options.data !== undefined ? ruleShapesFromOptions(options.data) : undefined;

  const ownFilingDetails = new Map<string, OwnFilingRecords>();
  shownOwnFilingIds.forEach((filingId, index) => {
    const detail = decodeOwnFilingDetail(details[index]?.data);
    ownFilingDetails.set(filingId, {
      cycles: detail.cycles,
      events: detail.events,
      firstEnteredNumber: detail.firstEnteredNumber,
      numberCorrections: detail.numberCorrections,
    });
  });
  const records: FilingsRecords = {
    obligations: extractObligations(obligations.data),
    occurrences: extractOccurrences(occurrences.data),
    cycles: extractRenewalCycles(cycles.data),
    imports: extractRegistrationImports(imports.data),
    ownFilings: extractOwnFilings(ownFilings.data),
    ownFilingDetails,
    groups: extractFilingGroups(groups.data),
    profile: decodeProfile(profile.data),
    ...(ruleShapes !== undefined ? { ruleShapes } : {}),
    today: localIsoDate(new Date()),
  };
  const rows = buildFilingRows({
    obligations: records.obligations,
    occurrences: records.occurrences,
    cycles: records.cycles,
    imports: records.imports,
    ownFilings: records.ownFilings,
    ownFilingCycles: new Map(
      [...ownFilingDetails].map(([filingId, detail]) => [filingId, detail.cycles] as const),
    ),
    profile: records.profile,
    ...(ruleShapes !== undefined ? { ruleShapes } : {}),
    today: records.today,
  });
  const followThrough =
    submissions.data !== undefined
      ? followThroughIndex(
          submissions.data.flatMap((raw) => {
            const decoded = decodeCaseFollowThrough(raw);
            return decoded === undefined ? [] : [decoded];
          }),
        )
      : NO_FOLLOW_THROUGH;
  const followed = withLatestSends(rows, followThrough);
  return {
    loading: false,
    failed: undefined,
    moduleDark: false,
    records,
    rows: followed,
    groups: composeFilingGroups(followed, records.groups),
    followThrough,
    refreshing: obligations.isFetching || ownFilings.isFetching,
  };
}

/**
 * CCM-R1-LATE-FILINGS (D-CCM-62): the year a filing's form or send review was opened for (the
 * `?cycle=` an overdue year's links carry, `FILING_YEAR_SEARCH_KEY`), else none: the page then takes
 * the year the filing's next date names.
 */
export function useChosenCycleId(): string | undefined {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const value = search[FILING_YEAR_SEARCH_KEY];
  return typeof value === 'string' && value !== '' ? value : undefined;
}

/** The search that keeps a page on the year it was opened for, as it moves between form and send. */
export function chosenYearSearch(cycleId: string | undefined): Record<string, string> {
  return cycleId !== undefined ? { [FILING_YEAR_SEARCH_KEY]: cycleId } : {};
}

/**
 * The same, spread into a navigation: nothing where no year was chosen, so a page opened for its
 * filing's next date moves exactly as it always has.
 */
export function withChosenYear(cycleId: string | undefined): { search?: Record<string, string> } {
  return cycleId !== undefined ? { search: chosenYearSearch(cycleId) } : {};
}
